import { useEffect, useState, type RefObject } from 'react'

/**
 * Left / right scroll buttons for the home carousels.
 *
 * Visible only at `lg:` (≥ 1024px) and up — phones and small tablets
 * rely on native touch swiping and don't get the chrome. Computer
 * and TV-class screens, where swiping isn't the primary input, get
 * full buttons they can click with a mouse/remote.
 *
 * The component owns the scroll logic: given a ref to the horizontal
 * scroll container, clicking an arrow pans the container by ~85% of
 * its visible width (one "page") with smooth scrolling. Disabled
 * states at either end of the range so the arrows gray out when
 * there's nowhere left to scroll.
 */

interface Props {
  scrollRef: RefObject<HTMLElement | null>
  /** ARIA label prefix — e.g. "Top for You — Movies" */
  ariaLabel: string
}

export function CarouselNav({ scrollRef, ariaLabel }: Props) {
  const [atStart, setAtStart] = useState(true)
  const [atEnd, setAtEnd] = useState(false)

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    const update = () => {
      setAtStart(el.scrollLeft <= 2)
      setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 2)
    }

    update()
    el.addEventListener('scroll', update, { passive: true })
    // Also update when the content size changes (lazy posters load,
    // items get revealed, filter changes).
    const resizeObserver = new ResizeObserver(update)
    resizeObserver.observe(el)

    return () => {
      el.removeEventListener('scroll', update)
      resizeObserver.disconnect()
    }
  }, [scrollRef])

  const scrollByPage = (direction: -1 | 1) => {
    const el = scrollRef.current
    if (!el) return
    el.scrollBy({ left: direction * el.clientWidth * 0.85, behavior: 'smooth' })
  }

  return (
    <>
      <button
        type="button"
        onClick={() => scrollByPage(-1)}
        disabled={atStart}
        aria-label={`Scroll ${ariaLabel} left`}
        className="absolute left-2 top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-black/70 text-white shadow-cinematic backdrop-blur-sm transition-all hover:scale-110 hover:border-accent-primary/60 hover:bg-black/90 disabled:pointer-events-none disabled:opacity-0 lg:flex"
      >
        <ChevronLeftIcon />
      </button>
      <button
        type="button"
        onClick={() => scrollByPage(1)}
        disabled={atEnd}
        aria-label={`Scroll ${ariaLabel} right`}
        className="absolute right-2 top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-black/70 text-white shadow-cinematic backdrop-blur-sm transition-all hover:scale-110 hover:border-accent-primary/60 hover:bg-black/90 disabled:pointer-events-none disabled:opacity-0 lg:flex"
      >
        <ChevronRightIcon />
      </button>
    </>
  )
}

function ChevronLeftIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="15 18 9 12 15 6" />
    </svg>
  )
}

function ChevronRightIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  )
}
